import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTooltipHeader } from '@mylighthouse/prism-ember';
import { formatNumber, t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import type {
  BENCHMARK,
  PARITY_FILTER,
} from 'lumen/utils/constants/pi-constant-values';

type SplitBenchmarkData = {
  [PARITY_FILTER.WIN]: number;
  [PARITY_FILTER.MEET]: number;
  [PARITY_FILTER.LOSS]: number;
};

interface ParityTooltipsTooltipKpiSignature {
  Args: {
    data: {
      [PARITY_FILTER.WIN]?: { value: number };
      [PARITY_FILTER.MEET]?: { value: number };
      [PARITY_FILTER.LOSS]?: { value: number };
      [BENCHMARK]?: number | SplitBenchmarkData | null;
      hasNoData?: boolean;
      singleValue?: number;
      totalNbOfHotels?: number;
      totalNbOfShops: number;
    };
    displayName: string;
    hideNbOfShops?: boolean;
    hideShoppedHotels?: boolean;
    noBenchmarkForBlock?: boolean;
    splitBenchmark?: boolean;
  };
}

export default class ParityTooltipsTooltipKpi extends Component<ParityTooltipsTooltipKpiSignature> {
  @service declare intl: Services['intl'];

  get benchmarkData(): number {
    return this.args.data.benchmark as number;
  }

  get hasBenchmark(): boolean {
    // Benchmark data has to be defined and segmentation block has to allow benchmark (~BLOCKS_WITH_BENCHMARK)
    return Boolean(this.args.data?.benchmark) && !this.args.noBenchmarkForBlock;
  }

  get hasNoData(): boolean {
    return (
      this.args.data === undefined || // Tooltip data is initially undefined for Parity Insight
      this.args.data.hasNoData ||
      this.args.data.totalNbOfShops === 0
    );
  }

  get isSingleValue(): boolean {
    return this.args.data?.singleValue !== undefined;
  }

  get nbOfHotels(): string {
    return this.intl.formatNumber(this.args.data?.totalNbOfHotels);
  }

  get nbOfShops(): string {
    return this.intl.formatNumber(this.args.data?.totalNbOfShops);
  }

  get splitBenchmarkData(): SplitBenchmarkData {
    return this.args.data.benchmark as SplitBenchmarkData;
  }

  <template>
    <div class="parity-tooltip-container" data-test-id="parity-tooltip-kpi">
      <PrismTooltipHeader>
        <span class="block px-200">{{@displayName}}</span>
      </PrismTooltipHeader>

      {{#if this.hasNoData}}
        <div class="no-data-box">
          <p class="text-200--regular truncate">{{t "general.no_data"}}</p>
        </div>
      {{else}}
        <div class="parity-metrics">
          {{#if this.isSingleValue}}
            <div class="box">
              <div class="value">
                <p class="text-200--bold truncate">
                  {{t "parity_analytics.percentage_total"}}
                </p>
                <p class="truncate">
                  {{formatNumber
                    @data.singleValue
                    minimumFractionDigits=2
                    style="percent"
                  }}
                </p>
              </div>
            </div>
          {{else}}
            {{#if (and this.hasBenchmark @splitBenchmark)}}
              <div class="box win-meet-loss">
                <div class="value">
                  <div class="text-200--bold flex justify-between gap-200">
                    <p class="truncate">{{t "parity_analytics.loss"}}</p>
                    <p class="truncate">
                      {{formatNumber @data.loss.value style="percent"}}
                    </p>
                  </div>
                  <div class="flex justify-between gap-200">
                    <p class="truncate">{{t "demand.benchmark"}}</p>
                    <p class="truncate">
                      {{formatNumber
                        this.splitBenchmarkData.loss
                        style="percent"
                      }}
                    </p>
                  </div>
                </div>
                <div class="value">
                  <div class="text-200--bold flex justify-between gap-200">
                    <p class="truncate">{{t "parity_analytics.meet"}}</p>
                    <p class="truncate">
                      {{formatNumber @data.meet.value style="percent"}}
                    </p>
                  </div>
                  <div class="flex justify-between gap-200">
                    <p class="truncate">{{t "demand.benchmark"}}</p>
                    <p class="truncate">
                      {{formatNumber
                        this.splitBenchmarkData.meet
                        style="percent"
                      }}
                    </p>
                  </div>
                </div>
                <div class="value">
                  <div class="text-200--bold flex justify-between gap-200">
                    <p class="truncate">{{t "parity_analytics.win"}}</p>
                    <p class="truncate">
                      {{formatNumber @data.win.value style="percent"}}
                    </p>
                  </div>
                  <div class="flex justify-between gap-200">
                    <p class="truncate">{{t "demand.benchmark"}}</p>
                    <p class="truncate">
                      {{formatNumber
                        this.splitBenchmarkData.win
                        style="percent"
                      }}
                    </p>
                  </div>
                </div>
              </div>
            {{else}}
              <div class="box win-meet-loss">
                <div class="value">
                  <p class="text-200--bold truncate">{{t
                      "parity_analytics.loss"
                    }}</p>
                  <p class="text-200--regular truncate">
                    {{formatNumber @data.loss.value style="percent"}}
                  </p>
                </div>
                {{#if @data.meet}}
                  <div class="value">
                    <p class="text-200--bold truncate">{{t
                        "parity_analytics.meet"
                      }}</p>
                    <p class="text-200--regular truncate">
                      {{formatNumber @data.meet.value style="percent"}}
                    </p>
                  </div>
                {{/if}}
                {{#if @data.win}}
                  <div class="value">
                    <p class="text-200--bold truncate">{{t
                        "parity_analytics.win"
                      }}</p>
                    <p class="text-200--regular truncate">
                      {{formatNumber @data.win.value style="percent"}}
                    </p>
                  </div>
                {{/if}}
                {{#if this.hasBenchmark}}
                  <div class="value gap-100">
                    <p class="text-200--bold truncate">{{t
                        "demand.benchmark"
                      }}</p>
                    <p class="text-200--regular truncate">
                      {{formatNumber this.benchmarkData style="percent"}}
                    </p>
                  </div>
                {{/if}}
              </div>
            {{/if}}
          {{/if}}

          <div class="box flex items-center justify-center">
            {{#unless @hideShoppedHotels}}
              <div class="value">
                <p class="text-200--bold truncate">{{t "general.hotels"}}</p>
                <p class="text-200--regular truncate">{{this.nbOfHotels}}</p>
              </div>
            {{/unless}}
            {{#unless @hideNbOfShops}}
              <div class="value">
                <p class="text-200--bold truncate">{{t
                    "parity_analytics.number_of_shops"
                  }}</p>
                <p class="text-200--regular truncate">{{this.nbOfShops}}</p>
              </div>
            {{/unless}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
